import React from 'react';
import styles from './FiscalInvoices.module.css';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Tooltip,
    Typography,
} from '@mui/material';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ModuleCard from '../../../appointment/components/moduleCard/ModuleCard';
import ModuleEmptyState from '../../../appointment/components/moduleCard/ModuleEmptyState';
import ColorPalette from '../../../../colorpalette/ColorPalette';
import { GetFiscalInvoicesByPatientAPI, GetFiscalPublicPdfUrl } from '../../../../api/FiscalAPI';
import { SendWhatsAppMessage } from '../../../../api/WhatsappAPI';
import MessagesTemplate from '../../../../consts/MessagesTemplate';
import FiscalInvoicePopup from '../../../../popup/FiscalInvoicePopup/FiscalInvoicePopup';
import CalendarConfig from '../../../../consts/CalendarConfig';

// ─── Nomes dos meses em português (reutiliza CalendarConfig.Months) ───────────

const MONTH_NAMES = CalendarConfig.Months;

// ─── Componente interno: item de nota fiscal ──────────────────────────────────

const FiscalInvoiceItem = ({ invoice, patientData, onViewDetails }) => {
    const formattedDate = invoice.issued_at
        ? new Date(invoice.issued_at).toLocaleDateString('pt-BR')
        : '—';

    const formattedAmount = invoice.amount != null
        ? `R$ ${Number(invoice.amount).toFixed(2).replace('.', ',')}`
        : '—';

    const pdfUrl = invoice.public_key ? GetFiscalPublicPdfUrl(invoice.public_key) : null;

    const handleOpenPdf = (e) => {
        e.stopPropagation();
        if (pdfUrl) window.open(pdfUrl, '_blank', 'noreferrer');
    };

    return (
        <div className={styles.invoiceItem}>
            {/* Lado esquerdo: badge + infos */}
            <div className={styles.invoiceItemLeft}>
                <Chip
                    label="Emitida"
                    size="small"
                    sx={{
                        backgroundColor: '#e8f5e9',
                        color: '#2e7d32',
                        fontWeight: 600,
                        fontSize: '11px',
                        height: '22px',
                        borderRadius: '6px',
                        flexShrink: 0,
                    }}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ColorPalette.textDark }}>
                        NF {invoice.invoice_number || `#${invoice.id}`}
                    </Typography>
                    <Typography variant="body2" sx={{ color: ColorPalette.textMedium, fontSize: '13px' }}>
                        {formattedDate} · {formattedAmount}
                    </Typography>
                </Box>
            </div>

            {/* Lado direito: ações */}
            <div className={styles.invoiceItemRight}>
                {/* Abrir PDF em nova aba */}
                {pdfUrl && (
                    <Tooltip title="Abrir PDF">
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={handleOpenPdf}
                            startIcon={<PictureAsPdfOutlinedIcon fontSize="small" />}
                            sx={{
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: '12px',
                                color: '#c62828',
                                borderColor: '#ef9a9a',
                                '&:hover': {
                                    backgroundColor: '#ffebee',
                                    borderColor: '#c62828',
                                },
                            }}
                        >
                            PDF
                        </Button>
                    </Tooltip>
                )}

                {/* Ver detalhes completos no popup */}
                <Tooltip title="Ver detalhes e mais opções">
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => onViewDetails(invoice)}
                        startIcon={<InfoOutlinedIcon fontSize="small" />}
                        sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '12px',
                        }}
                    >
                        Ver detalhes
                    </Button>
                </Tooltip>
            </div>
        </div>
    );
};

// ─── Botão WhatsApp padronizado ───────────────────────────────────────────────

const WhatsAppSendButton = ({ label, onClick, disabled, size = 'small', sx = {} }) => (
    <Tooltip title={disabled ? 'Paciente sem telefone cadastrado' : ''} arrow>
        <span>
            <Button
                variant="outlined"
                color="success"
                startIcon={<WhatsAppIcon />}
                onClick={onClick}
                disabled={disabled}
                size={size}
                sx={{
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    ...sx,
                }}
            >
                {label}
            </Button>
        </span>
    </Tooltip>
);

// ─── Componente principal ─────────────────────────────────────────────────────

const FiscalInvoices = ({ patientData }) => {
    const [fiscalInvoices, setFiscalInvoices] = React.useState([]);
    const [fiscalLoading, setFiscalLoading] = React.useState(false);

    // Nota selecionada para abrir no popup
    const [selectedInvoice, setSelectedInvoice] = React.useState(null);

    React.useEffect(() => {
        if (!patientData?.id) return;
        setFiscalLoading(true);
        GetFiscalInvoicesByPatientAPI(patientData.id, (result) => {
            setFiscalLoading(false);
            if (result?.success) setFiscalInvoices(result.invoices || []);
        });
    }, [patientData?.id]);

    // Monta um appointmentData mínimo para o FiscalInvoicePopup a partir da nota
    const buildAppointmentData = (invoice) => ({
        id: invoice.appointment_id,
        invoiceId: invoice.payment_id || null,
        price: invoice.amount,
        date: invoice.appointment_date || invoice.issued_at,
        procedure: null,
        resource: null,
    });

    const handleViewDetails = (invoice) => {
        setSelectedInvoice(invoice);
    };

    const handleClosePopup = () => {
        setSelectedInvoice(null);
        // Recarrega a lista para refletir eventuais cancelamentos feitos no popup
        if (patientData?.id) {
            setFiscalLoading(true);
            GetFiscalInvoicesByPatientAPI(patientData.id, (result) => {
                setFiscalLoading(false);
                if (result?.success) setFiscalInvoices(result.invoices || []);
            });
        }
    };

    // ─── Agrupamento por ano e mês ────────────────────────────────────────────

    const groupedInvoices = React.useMemo(() => {
        const groups = {};

        fiscalInvoices.forEach(inv => {
            const date = inv.appointment_date || inv.issued_at;
            if (!date) return;

            const d = new Date(date);
            const year = d.getFullYear();
            const month = d.getMonth(); // 0-11

            if (!groups[year]) groups[year] = {};
            if (!groups[year][month]) groups[year][month] = [];
            groups[year][month].push(inv);
        });

        // Converter para array ordenado (mais recente primeiro)
        return Object.keys(groups)
            .sort((a, b) => b - a) // anos decrescentes
            .map(year => ({
                year: parseInt(year),
                months: Object.keys(groups[year])
                    .sort((a, b) => b - a) // meses decrescentes
                    .map(month => ({
                        month: parseInt(month),
                        invoices: groups[year][month],
                    })),
            }));
    }, [fiscalInvoices]);

    // ─── Envio por WhatsApp por grupo ─────────────────────────────────────────

    const handleSendByWhatsApp = (invoices, label) => {
        const phone = patientData?.phone;
        if (!phone || invoices.length === 0) return;

        let message = `Olá! Seguem suas notas fiscais de ${label}:\n\n`;

        invoices.forEach((inv, idx) => {
            const date = inv.appointment_date || inv.issued_at;
            const dateStr = date ? new Date(date).toLocaleDateString('pt-BR') : '';
            const amount = inv.amount != null
                ? `R$ ${Number(inv.amount).toFixed(2).replace('.', ',')}`
                : '';
            const url = inv.public_key ? GetFiscalPublicPdfUrl(inv.public_key) : null;

            message += `${idx + 1}. NF ${inv.invoice_number || inv.id} — ${dateStr} — ${amount}`;
            if (url) message += `\n${url}`;
            message += `\n\n`;
        });

        message += `Em caso de dúvidas, estamos à disposição.`;

        const encoded = MessagesTemplate.ConvertToWhatsAppText(message);
        // DDI do paciente — sem ele, DDD 33 (por exemplo) é lido como +33 (França)
        SendWhatsAppMessage(phone, encoded, patientData?.countryCode);
    };

    const hasPhone = Boolean(patientData?.phone);

    // ─── Contador de notas ────────────────────────────────────────────────────

    const noteCount = (n) => `${n} ${n === 1 ? 'nota' : 'notas'}`;

    // "Enviar todas" fica no cabeçalho do card, ao lado do título
    const sendAllAction = fiscalInvoices.length > 0 && !fiscalLoading ? (
        <WhatsAppSendButton
            label={`Enviar todas (${noteCount(fiscalInvoices.length)})`}
            onClick={() => handleSendByWhatsApp(fiscalInvoices, 'todos os períodos')}
            disabled={!hasPhone}
        />
    ) : null;

    return (
        <>
            <ModuleCard
                title="Notas fiscais"
                icon={<ReceiptOutlinedIcon />}
                description="Notas fiscais eletrônicas emitidas"
                accent="amber"
                actions={sendAllAction}
            >
                {fiscalLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
                        <CircularProgress size={32} />
                    </Box>
                ) : fiscalInvoices.length === 0 ? (
                    <ModuleEmptyState icon={<ReceiptOutlinedIcon />} text="Nenhuma nota fiscal emitida." />
                ) : (
                    <div className={styles.container}>
                        {/* Grupos por ano */}
                        {groupedInvoices.map(yearGroup => {
                            const totalInYear = yearGroup.months.reduce((acc, m) => acc + m.invoices.length, 0);

                            return (
                                <div key={yearGroup.year} className={styles.yearGroup}>
                                    {/* Cabeçalho do ano */}
                                    <div className={styles.yearHeader}>
                                        <div className={styles.yearTitleRow}>
                                            <span className={styles.yearTitle}>{yearGroup.year}</span>
                                            <span className={styles.yearCount}>{noteCount(totalInYear)}</span>
                                        </div>
                                        <WhatsAppSendButton
                                            label="Enviar ano"
                                            onClick={() => {
                                                const allInYear = yearGroup.months.flatMap(m => m.invoices);
                                                handleSendByWhatsApp(allInYear, `todo ${yearGroup.year}`);
                                            }}
                                            disabled={!hasPhone}
                                            sx={{ fontSize: '0.75rem', fontWeight: 500 }}
                                        />
                                    </div>

                                    {/* Grupos por mês dentro do ano */}
                                    <div className={styles.monthsContainer}>
                                        {yearGroup.months.map(monthGroup => (
                                            <div key={monthGroup.month} className={styles.monthGroup}>
                                                {/* Cabeçalho do mês */}
                                                <div className={styles.monthHeader}>
                                                    <div className={styles.monthTitleRow}>
                                                        <span className={styles.monthTitle}>
                                                            {MONTH_NAMES[monthGroup.month]} {yearGroup.year}
                                                        </span>
                                                        <span className={styles.monthCount}>
                                                            {noteCount(monthGroup.invoices.length)}
                                                        </span>
                                                    </div>
                                                    <WhatsAppSendButton
                                                        label="Enviar mês"
                                                        onClick={() => handleSendByWhatsApp(
                                                            monthGroup.invoices,
                                                            `${MONTH_NAMES[monthGroup.month]} ${yearGroup.year}`
                                                        )}
                                                        disabled={!hasPhone}
                                                        sx={{ fontSize: '0.7rem', fontWeight: 500 }}
                                                    />
                                                </div>

                                                {/* Lista de notas do mês */}
                                                <div className={styles.invoiceList}>
                                                    {monthGroup.invoices.map(inv => (
                                                        <FiscalInvoiceItem
                                                            key={inv.id}
                                                            invoice={inv}
                                                            patientData={patientData}
                                                            onViewDetails={handleViewDetails}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </ModuleCard>

            {/* Popup de detalhes da nota fiscal */}
            {selectedInvoice && (
                <FiscalInvoicePopup
                    appointmentData={buildAppointmentData(selectedInvoice)}
                    patientData={patientData}
                    onHide={handleClosePopup}
                />
            )}
        </>
    );
};

export default FiscalInvoices;
